import { useState, useEffect } from "react";
import { Outlet, NavLink } from "react-router-dom";
import { SquaresFour, Palette, Users, CreditCard, UserCircle, SignOut, List, X } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { applyBrandColor } from "@/lib/color";

const Layout = () => {
  const { user, logout, org } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  // The org's chosen brand colour drives every `var(--brand)` /
  // `var(--brand-dark)` Tailwind class across the authenticated app (nav
  // highlight, buttons, etc.) — set once here since Layout wraps every
  // dashboard route, and re-applied whenever the org's colour changes.
  useEffect(() => {
    applyBrandColor(org?.brand_color);
  }, [org?.brand_color]);

  const navItems = [
    { path: "/", label: "Dashboard", icon: SquaresFour },
    { path: "/designs", label: "Pass Designs", icon: Palette },
    { path: "/members", label: "Members", icon: Users },
    {
      path: "/settings/account",
      label: "Account",
      icon: UserCircle,
      children: [{ path: "/settings/billing", label: "Billing", icon: CreditCard }],
    },
  ];

  const navLinks = (
    <nav className="px-4 space-y-1 flex-1" data-testid="main-navigation">
      {navItems.map((item) => {
        const Icon = item.icon;
        return (
          <div key={item.path} className="space-y-1">
            <NavLink
              to={item.path}
              end
              onClick={() => setMobileOpen(false)}
              data-testid={`nav-${item.label.toLowerCase().replace(" ", "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-md transition-colors ${
                  isActive
                    ? "bg-[var(--brand)] text-white"
                    : "text-zinc-700 hover:bg-zinc-100"
                }`
              }
            >
              <Icon size={20} weight="regular" />
              <span className="font-medium text-sm">{item.label}</span>
            </NavLink>
            {item.children?.map((child) => {
              const ChildIcon = child.icon;
              return (
                <NavLink
                  key={child.path}
                  to={child.path}
                  onClick={() => setMobileOpen(false)}
                  data-testid={`nav-${child.label.toLowerCase().replace(" ", "-")}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 ml-8 px-3 py-2 rounded-md transition-colors ${
                      isActive
                        ? "bg-[var(--brand)] text-white"
                        : "text-zinc-500 hover:bg-zinc-100 hover:text-zinc-700"
                    }`
                  }
                >
                  <ChildIcon size={16} weight="regular" />
                  <span className="font-medium text-sm">{child.label}</span>
                </NavLink>
              );
            })}
          </div>
        );
      })}
    </nav>
  );

  const accountFooter = (
    <div className="p-4 border-t border-zinc-200">
      <div className="flex items-center justify-between px-2 mb-3">
        <span className="text-xs text-zinc-500 truncate">{user?.email}</span>
      </div>
      <button
        onClick={logout}
        className="flex items-center gap-3 w-full px-4 py-2.5 rounded-md text-zinc-500 hover:bg-zinc-100 hover:text-zinc-700 transition-colors"
        data-testid="logout-btn"
      >
        <SignOut size={18} weight="regular" />
        <span className="font-medium text-sm">Sign Out</span>
      </button>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-white">
      {/* Mobile top bar (hidden on lg+) */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-30 flex items-center justify-between px-4 py-3 border-b border-zinc-200 bg-white">
        <span className="text-lg font-semibold tracking-tight text-zinc-900">
          Club Pass
        </span>
        <button
          onClick={() => setMobileOpen(true)}
          className="p-2 -mr-2 rounded-md text-zinc-700 hover:bg-zinc-100"
          data-testid="mobile-menu-btn"
          aria-label="Open menu"
        >
          <List size={24} weight="bold" />
        </button>
      </div>

      {/* Mobile off-canvas drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div
            className="fixed inset-0 bg-black/40"
            onClick={() => setMobileOpen(false)}
            data-testid="mobile-menu-overlay"
          />
          <aside className="relative z-50 flex flex-col h-full w-64 max-w-[80vw] bg-white shadow-xl">
            <div className="flex items-center justify-between p-6">
              <div>
                <h1 className="text-xl font-semibold tracking-tight text-zinc-900">
                  Club Pass
                </h1>
                <p className="text-xs text-zinc-500 mt-1">Designer & Issuer</p>
              </div>
              <button
                onClick={() => setMobileOpen(false)}
                className="p-1 rounded-md text-zinc-500 hover:bg-zinc-100"
                data-testid="mobile-menu-close-btn"
                aria-label="Close menu"
              >
                <X size={20} weight="bold" />
              </button>
            </div>
            {navLinks}
            {accountFooter}
          </aside>
        </div>
      )}

      {/* Desktop sidebar (lg and up) */}
      <aside
        className="hidden lg:flex w-64 shrink-0 border-r border-zinc-200 bg-white flex-col"
        data-testid="sidebar"
      >
        <div className="p-8">
          <h1 className="text-2xl font-semibold tracking-tight text-zinc-900">
            Club Pass
          </h1>
          <p className="text-sm text-zinc-500 mt-1">Designer & Issuer</p>
        </div>
        {navLinks}
        {accountFooter}
      </aside>

      <main className="flex-1 min-w-0 pt-14 lg:pt-0">
        <Outlet />
      </main>
    </div>
  );
};

export default Layout;
